import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Search, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API_URL, analytics, geocodePlace, isUsZipCode } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import LocationCard from "@/components/LocationCard";

const LOCATIONS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Jersey Girls Fitness",
  "url": "https://jerseygirlsclubs.com/locations",
  "description": "Women-only fitness centers across New Jersey and Philadelphia. Find your nearest Jersey Girls location.",
  "areaServed": ["New Jersey", "Philadelphia, PA"]
};

export default function LocationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  // When set, we're showing "closest to" this label (e.g. "you", "Hoboken")
  const [nearestLabel, setNearestLabel] = useState(null);

  const fetchLocations = useCallback(async () => {
    setFetchError(null);
    try {
      const response = await axios.get(`${API_URL}/locations`);
      setLocations(response.data);
      setFilteredLocations(response.data);
      setNearestLabel(null);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
      setFetchError(error.message || "Unable to load locations.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const showAllLocations = useCallback(() => {
    setFilteredLocations(locations);
    setNearestLabel(null);
    setSearchQuery("");
    setSearchParams({});
  }, [locations, setSearchParams]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // On load with ?q=, show closest to that place (e.g. zip code)
  const qParam = searchParams.get("q");
  useEffect(() => {
    if (!qParam?.trim() || locations.length === 0) return;
    let cancelled = false;
    (async () => {
      setNearestLabel(null);
      let coords = await geocodePlace(qParam);
      if (cancelled) return;
      if (!coords && isUsZipCode(qParam)) {
        const zip = qParam.trim();
        const match = locations.find((loc) => (loc.zip_code || "").replace(/\s/g, "") === zip.replace(/\s/g, "") || (loc.zip_code || "").startsWith(zip.slice(0, 5)));
        if (match && typeof match.latitude === "number" && typeof match.longitude === "number") {
          coords = { lat: match.latitude, lng: match.longitude };
        }
      }
      if (coords) {
        try {
          const response = await axios.get(`${API_URL}/locations/nearest`, {
            params: { lat: coords.lat, lng: coords.lng, limit: 50 }
          });
          if (!cancelled) {
            setFilteredLocations(response.data);
            setNearestLabel(qParam.trim());
          }
        } catch (err) {
          if (!cancelled) setFilteredLocations(locations);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [qParam, locations]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      showAllLocations();
      return;
    }
    analytics.trackLocationSearch(query);
    setIsLoading(true);
    setFetchError(null);
    try {
      let coords = await geocodePlace(query);
      // If geocode failed but query is a US zip, use a matching location's coords so "nearest to zip" still works
      if (!coords && isUsZipCode(query) && locations.length > 0) {
        const zip = query.trim();
        const match = locations.find((loc) => (loc.zip_code || "").replace(/\s/g, "") === zip.replace(/\s/g, "") || (loc.zip_code || "").startsWith(zip.slice(0, 5)));
        if (match && typeof match.latitude === "number" && typeof match.longitude === "number") {
          coords = { lat: match.latitude, lng: match.longitude };
        }
      }
      if (coords) {
        const response = await axios.get(`${API_URL}/locations/nearest`, {
          params: { lat: coords.lat, lng: coords.lng, limit: 50 }
        });
        setFilteredLocations(response.data);
        setNearestLabel(query);
        setSearchParams({ q: query });
      } else {
        const response = await axios.get(`${API_URL}/locations/search`, { params: { query } });
        setFilteredLocations(response.data);
        setNearestLabel(null);
        setSearchParams({ q: query });
      }
    } catch (error) {
      console.error("Search failed:", error);
      setFetchError(error.message || "Search failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setIsLoading(true);
    setFetchError(null);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.get(`${API_URL}/locations/nearest`, {
            params: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              limit: 50
            }
          });
          setFilteredLocations(response.data);
          setNearestLabel("you");
          setSearchQuery("");
          setSearchParams({});
        } catch (error) {
          console.error("Failed to fetch nearest locations:", error);
          setFetchError("Unable to load nearest locations.");
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setIsLoading(false);
        alert("Unable to get your location. Please search by zip code or city.");
      }
    );
  };

  const navigate = useNavigate();
  const handleSignupClick = (location) => {
    analytics.trackSignupClick(location.id, location.name);
    navigate("/join-thank-you");
  };

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-0" data-testid="locations-page">
      <SEO
        title="Find Your Fitness Center"
        description="Find your nearest Jersey Girls location in New Jersey and Philadelphia. Women-only fitness centers with premium equipment and free group strength training. Search by zip code or use your location."
        url="/locations"
        structuredData={LOCATIONS_SCHEMA}
      />
      {/* Header */}
      <section className="py-12 md:py-20 px-4 md:px-8 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-8" data-testid="locations-heading">
              FIND YOUR FITNESS CENTER
            </h1>

            {/* Search Bar */}
            <div className="flex flex-col gap-3 max-w-2xl">
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Search by zip code, city, or name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 rounded-lg bg-white border-gray-300 focus:border-brand-pink text-gray-900 placeholder:text-gray-500"
                    data-testid="locations-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 px-6 rounded-lg bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider shadow-soft hover:shadow-neon-pink transition-all"
                  data-testid="locations-search-btn"
                >
                  Search
                </Button>
              </form>

              <Button
                variant="outline"
                onClick={handleUseMyLocation}
                className="h-12 w-full sm:w-auto border-gray-300 bg-white text-gray-800 hover:bg-gray-100 hover:border-gray-400"
                data-testid="locations-use-location-btn"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Use My Location
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="location-card h-80 skeleton" />
              ))}
            </div>
          ) : fetchError ? (
            <div className="text-center py-12" data-testid="locations-error">
              <MapPin className="w-16 h-16 mx-auto text-brand-pink mb-4" />
              <h3 className="text-xl font-heading text-gray-900 mb-2">Unable to Load Locations</h3>
              <p className="text-gray-700 mb-4">{fetchError}</p>
              <Button onClick={() => { setIsLoading(true); fetchLocations(); }} className="bg-brand-pink hover:bg-brand-pink-hover text-white">
                Try Again
              </Button>
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12" data-testid="no-locations-message">
              <MapPin className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">No Locations Found</h3>
              <p className="text-gray-400">Try a different search or use your current location.</p>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <p className="text-sm text-gray-700" data-testid="locations-count">
                  {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} found
                  {nearestLabel && (
                    <span className="ml-2 text-brand-pink">
                      — closest to {nearestLabel === "you" ? "you" : searchQuery || nearestLabel}
                    </span>
                  )}
                </p>
                {nearestLabel && (
                  <Button
                    variant="link"
                    className="text-gray-600 hover:text-gray-900 p-0 h-auto text-sm"
                    onClick={showAllLocations}
                    data-testid="show-all-locations"
                  >
                    Show all locations
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-grid">
                {filteredLocations.map((location, index) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    index={index}
                    onSignupClick={handleSignupClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

